/**
 * App entry: mounts providers (access, active-portfolio), the AppShell chrome,
 * and the route table. Every route page is lazy-loaded as its own chunk;
 * RouteGate enforces per-route access rules.
 */
import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';

import { AppShell } from '@/components/AppShell';
import { RouteGate } from '@/components/RouteGate';
import { AccessProvider } from '@/lib/access';
import { ActivePortfolioProvider } from '@/lib/activePortfolio';
import { initSiteAnalytics, recordPageView } from '@/lib/siteAnalytics';
import { initErrorReporter } from '@/lib/errorReporter';
import { initTheme } from '@/lib/theme';

// Inherited stylesheet FIRST so Tailwind (index.css) overrides its
// `* { margin: 0; padding: 0 }` reset — otherwise it wins on the AppShell sidebar.
import '@/core/inherited-styles.css';
import './index.css';

// Route pages are lazy-loaded (one chunk each); named exports mapped to `default`.

// PV (research / analysis) pages
const BacktestPage = lazy(() => import('@/pages/Backtest').then((m) => ({ default: m.BacktestPage })));
const MonteCarloPage = lazy(() => import('@/pages/MonteCarlo').then((m) => ({ default: m.MonteCarloPage })));
const CorrelationsPage = lazy(() => import('@/pages/Correlations').then((m) => ({ default: m.CorrelationsPage })));
const FactorRegressionPage = lazy(() => import('@/pages/FactorRegression').then((m) => ({ default: m.FactorRegressionPage })));
const OptimisationPage = lazy(() => import('@/pages/Optimisation').then((m) => ({ default: m.OptimisationPage })));
const TacticalAllocationPage = lazy(() => import('@/pages/TacticalAllocation').then((m) => ({ default: m.TacticalAllocationPage })));
const TradingToolsPage = lazy(() => import('@/pages/TradingTools').then((m) => ({ default: m.TradingToolsPage })));
const FundScreenerPage = lazy(() => import('@/pages/FundScreener').then((m) => ({ default: m.FundScreenerPage })));

// Portfolio pages
// Performance Summary now hosts Calendar (dividends) / Market Themes / Global Indexes / Watchlist as subtabs.
const PerformanceSummaryPage = lazy(() => import('@/pages/PerformanceSummaryHub').then((m) => ({ default: m.PerformanceSummaryHub })));
const TradeHistoryPage = lazy(() => import('@/pages/TradeHistory').then((m) => ({ default: m.TradeHistoryPage })));
// Log Trade now hosts Import / Manual changes as subtabs.
const LogTradePage = lazy(() => import('@/pages/LogTradeHub').then((m) => ({ default: m.LogTradeHub })));
const SnapshotsPage = lazy(() => import('@/pages/Snapshots').then((m) => ({ default: m.SnapshotsPage })));
const JournalPage = lazy(() => import('@/pages/Journal').then((m) => ({ default: m.JournalPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })));
const AnalyticsPage = lazy(() => import('@/pages/Analytics').then((m) => ({ default: m.AnalyticsPage })));
const TaxPage = lazy(() => import('@/pages/Tax').then((m) => ({ default: m.TaxPage })));
const ScenarioGeneratorPage = lazy(() => import('@/pages/ScenarioGenerator').then((m) => ({ default: m.ScenarioGeneratorPage })));
const AdminPage = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.AdminPage })));
const PublicViewPage = lazy(() => import('@/pages/PublicView').then((m) => ({ default: m.PublicViewPage })));
const LoginPage = lazy(() => import('@/pages/Auth').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/Auth').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/Auth').then((m) => ({ default: m.ResetPasswordPage })));

/**
 * Fires an anonymous page-view on every route change. recordPageView no-ops
 * until initSiteAnalytics() runs (production only), so mounting it is always safe.
 */
function TelemetryTracker() {
  const location = useLocation();
  useEffect(() => {
    recordPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

/** Shown while a lazily-loaded route chunk is in flight. */
function RouteFallback() {
  return (
    <div style={{ padding: 24, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
      Loading…
    </div>
  );
}

// Apply saved theme (light/dark + accent) before first paint.
initTheme();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// Telemetry is production-only so dev/smoke runs don't post to the analytics stream.
if (import.meta.env.PROD) {
  initSiteAnalytics();
  initErrorReporter();
}

createRoot(root).render(
  <StrictMode>
    <BrowserRouter basename="/">
      <AccessProvider>
        <ActivePortfolioProvider>
        <TelemetryTracker />
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/performance-summary" replace />} />
              <Route path="/time-machine" element={<Navigate to="/performance-summary" replace />} />
              {/* Back-compat: Efficient Frontier is now a tab inside Optimisation. */}
              <Route path="/efficient-frontier" element={<Navigate to="/optimisation" replace />} />
              {/* Back-compat: tab routes renamed to match their labels. */}
              <Route path="/journal" element={<Navigate to="/investment-thesis" replace />} />
              {/* Market Themes is now a Performance Summary subtab. */}
              <Route path="/think-pieces" element={<Navigate to="/performance-summary/market-themes" replace />} />
              <Route path="/market-themes" element={<Navigate to="/performance-summary/market-themes" replace />} />
              <Route path="/tactical" element={<Navigate to="/tactical-allocation" replace />} />
              <Route path="/correlations" element={<Navigate to="/asset-correlations" replace />} />
              <Route path="/fund-screener" element={<Navigate to="/fund-research" replace />} />
              <Route path="/public" element={<Navigate to="/public-view" replace />} />

              {/* Auth screens are never gated (gating /login would loop). */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Every tab route goes through the access guard (RouteGate + lib/nav). */}
              <Route element={<RouteGate />}>
                <Route path="/performance-summary" element={<PerformanceSummaryPage />} />
                {/* Subtabs (dividends / market-themes / global-indexes / watchlist / projections) are addressable. */}
                <Route path="/performance-summary/:subtab" element={<PerformanceSummaryPage />} />
                {/* Projections addresses its inner panels by path too, so the
                    nightly alert digest can deep-link into a specific panel. */}
                <Route path="/performance-summary/:subtab/:panel" element={<PerformanceSummaryPage />} />
                <Route path="/log-trade" element={<LogTradePage />} />
                <Route path="/trade-history" element={<TradeHistoryPage />} />
                <Route path="/tax" element={<TaxPage />} />
                <Route path="/backtest" element={<BacktestPage />} />
                <Route path="/monte-carlo" element={<MonteCarloPage />} />
                <Route path="/asset-correlations" element={<CorrelationsPage />} />
                <Route path="/factor-regression" element={<FactorRegressionPage />} />
                <Route path="/optimisation" element={<OptimisationPage />} />
                <Route path="/tactical-allocation" element={<TacticalAllocationPage />} />
                <Route path="/trading-tools" element={<TradingToolsPage />} />
                <Route path="/fund-research" element={<FundScreenerPage />} />
                <Route path="/investment-thesis" element={<JournalPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/scenario-generator" element={<ScenarioGeneratorPage />} />
                <Route path="/snapshots" element={<SnapshotsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/public-view" element={<PublicViewPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/performance-summary" replace />} />
            </Routes>
          </Suspense>
        </AppShell>
        </ActivePortfolioProvider>
      </AccessProvider>
    </BrowserRouter>
  </StrictMode>,
);
